import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from './utils/fetchWithAuth';

const TableComponent = ({ title, columns, data }) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <h2 className="text-xl font-semibold text-blue-600 mb-4">{title}</h2>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {columns.map((column) => (
                <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item[column.dataIndex]}
                </td>
              ))}
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const Merch = () => {
  const { t } = useTranslation();
  const [projectKey, setProjectKey] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectKey) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`merch/${projectKey}`);

      if (!response.ok) {
        throw new Error('Project not found');
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tables = [
    {
      title: 'Project Information',
      dataSource: data?.project ? [data.project] : [],
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Key', dataIndex: 'key', key: 'key' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
      ]
    },
    {
      title: 'Service Desk Information',
      dataSource: data?.serviceDesk ? [data.serviceDesk] : [],
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'URL', dataIndex: 'url', key: 'url' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
      ]
    },
    {
      title: 'Request Types',
      dataSource: data?.requestTypes || [],
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Issue Type ID', dataIndex: 'issueTypeId', key: 'issueTypeId' },
      ]
    },
    {
      title: 'Form Types',
      dataSource: data?.formTypes || [],
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
      ]
    },
    {
      title: 'Forms',
      dataSource: data?.forms || [],
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
      ]
    },
    {
      title: 'Users',
      dataSource: data?.users || [],
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
      ]
    }
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="container mx-auto px-4 py-8">
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="flex max-w-xl">
              <input
                type="text"
                value={projectKey}
                onChange={(e) => setProjectKey(e.target.value)}
                placeholder={t('Enter Project Key')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t('Search')}
              </button>
            </div>
          </form>

          {loading && (
            <div className="flex justify-center my-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}

          {data && (
            <div className="space-y-8">
              {tables.map((table, index) => (
                <TableComponent
                  key={index}
                  title={table.title}
                  columns={table.columns}
                  data={table.dataSource}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Merch;
